import React, { useEffect, useState } from "react";
import { Title } from "rbx";
import PropTypes from "prop-types";
import { useTheme, useMediaQuery } from "@mui/material";
import { useQuery } from "@apollo/client";
import ReviewSection from "./components/ReviewSection/ReviewSection";
import EvaluationForm from "./components/EvaluationForm/EvaluationForm";
import { useModal } from "../../context";
import "./Reviews.scss";
import { usePermissions } from "../../hooks";
import { REVIEW_SECTIONS } from "./constants";
import { GET_REVIEWS } from "./graphql";

const CompletedReviews = ({ reviews, employeeNumber, loading, setReviews }) => {
  const { setModalOpen } = useModal();
  const permissionLevels = usePermissions();
  const [permissions, setPermissions] = useState(null);
  const [currentReviewState, setCurrentReviewState] = useState(null);
  const [currentEvaluationState, setCurrentEvaluationState] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const { data: reviewData, loading: reviewLoading, refetch } = useQuery(GET_REVIEWS, {
    variables: {
      where: {
        id: { equals: currentReviewState?.id },
        evaluations: {
          some: {
            reviewId: { equals: currentReviewState?.id },
            id: { equals: currentEvaluationState?.id }
          }
        }
      },
      evaluationWhere: currentEvaluationState?.id
        ? { id: { equals: currentEvaluationState.id } }
        : undefined,
      sectionsOrderBy: [{ order: "asc" }],
      questionsOrderBy: [{ order: "asc" }],
    },
    skip:  !currentEvaluationState?.id,
    fetchPolicy: "cache-and-network",
    context: { fetchOptions: { timeout: 60000 } }

  });


  const showManagerReviews =
    permissions?.webEditor ||
    permissions?.superAdmin ||
    permissions?.admin ||
    permissions?.fieldSupervisor;

  useEffect(() => {
    if (permissionLevels) {
      setPermissions(permissionLevels);
    }
  }, [permissionLevels]);

  const handleOpenReview = (evaluation) => {
    setIsLoading(true);
    const currentReview = reviews.filter(
      (review) => review.id === evaluation?.reviewId
    );

    setCurrentReviewState(currentReview[0]);
    setCurrentEvaluationState({ ...evaluation, isSigning: false });

  };

  useEffect(() => {
    if (reviewData?.reviews?.[0] && currentEvaluationState && isLoading) {

      const currentEvaluation = reviewData.reviews[0].evaluations.find(
        e => e.id === currentEvaluationState.id
      );

      if (currentEvaluation) {
        setIsLoading(false);
        setModalOpen(
          true,
          <EvaluationForm
            currentReviewId={currentEvaluationState.reviewId}
            employeeNumber={employeeNumber}
            evaluation={currentEvaluation}
            evaluations={reviewData.reviews[0].evaluations}
            isSigning={currentEvaluationState.isSigning}
            key={currentEvaluation.id}
            loading={false}
            reviews={reviews}
            onCanceled={() => setModalOpen(false)}
            onComplete={() => setModalOpen(false)}

          />
        );
      } else {
        setIsLoading(false);
      }
    }
  }, [reviewData, currentEvaluationState, isLoading]);

  useEffect(() => {
    if (isLoading && (!reviewData?.reviews?.[0] || !currentEvaluationState)) {
      setModalOpen(
        true,
        <div className="modal-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <div className="loader" />
        </div>
      );
    }
  }, [isLoading, reviewData, currentEvaluationState, setModalOpen]);


  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down("sm"));

  const className = showManagerReviews
    ? `reviews-outer-container-manager ${downSm && "grid-one-fr"}`
    : `reviews-outer-container ${downSm && "grid-one-fr"}`;

  return (
    <div className={className}>
      {REVIEW_SECTIONS.map(
        (section) =>
          ((showManagerReviews && section.sectionType === "Manager") ||
            section.sectionType !== "Manager") && (
            <div className="reviews-container-section" key={section?.title}>
              <div className="reviews">
                <div className="centered-title" id="employee-reviews">
                  <Title size={4}>{section.title}</Title>
                </div>
                {reviews.length ? (
                  <ReviewSection
                    isCompletedSection
                    handleClick={handleOpenReview}
                    reviews={reviews}
                    sectionType={section.sectionType}
                    setReviews={setReviews}
                  />
                ) : (
                  <div className="centered-title">
                    <Title size={6}>
                      No completed {section.sectionType.toLowerCase()}{" "}
                      evaluations found
                    </Title>
                  </div>
                )}
              </div>
            </div>
          )
      )}
    </div>
  );
};

CompletedReviews.propTypes = {
  reviews: PropTypes.array.isRequired,
  employeeNumber: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  setReviews: PropTypes.func.isRequired,
};

export default CompletedReviews;
